import type {
  MarginaliaExportV1,
  MarginaliaLibraryExportV1,
} from "./schemas/v1";
import { serializeDocumentExport } from "./export-document";
import { serializeLibraryExport } from "./export-library";

export const MARGINALIA_JSON_MIME_TYPE =
  "application/vnd.marginalia.annotations+json";
export const MARGINALIA_LIBRARY_JSON_MIME_TYPE =
  "application/vnd.marginalia.library+json";

const WINDOWS_RESERVED_NAMES =
  /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;

function truncateCodePoints(value: string, maximum: number): string {
  return Array.from(value).slice(0, maximum).join("");
}

function replaceUnsafeFileNameCharacters(value: string): string {
  const reserved = new Set(['<', '>', ':', '"', '/', "\\", "|", "?", "*"]);

  return Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f || reserved.has(character)
      ? "-"
      : character;
  }).join("");
}

export function sanitizeDocumentName(fileName: string): string {
  const withoutPdf = fileName.trim().replace(/\.pdf$/i, "");
  let sanitized = replaceUnsafeFileNameCharacters(withoutPdf.normalize("NFKC"))
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-")
    .trim()
    .replace(/^[.\s-]+|[.\s-]+$/g, "");

  sanitized = truncateCodePoints(sanitized, 100).trim();

  if (sanitized.length === 0) {
    return "document";
  }

  if (WINDOWS_RESERVED_NAMES.test(sanitized)) {
    return `document-${sanitized}`;
  }

  return sanitized;
}

export function defaultDocumentExportFileName(fileName: string): string {
  return `${sanitizeDocumentName(fileName)}.marginalia.json`;
}

export function defaultLibraryExportFileName(exportedAt: string): string {
  return `marginalia-library-${exportedAt.slice(0, 10)}.json`;
}

export function createDocumentExportBlob(
  bundle: MarginaliaExportV1,
  mimeType = MARGINALIA_JSON_MIME_TYPE,
): Blob {
  return new Blob([serializeDocumentExport(bundle)], {
    type: mimeType,
  });
}

export function createLibraryExportBlob(
  bundle: MarginaliaLibraryExportV1,
  mimeType = MARGINALIA_LIBRARY_JSON_MIME_TYPE,
): Blob {
  return new Blob([serializeLibraryExport(bundle)], {
    type: mimeType,
  });
}

export type PreparedExportDownload = {
  blob: Blob;
  fileName: string;
  url: string;
  revoke(): void;
};

export type CreateExportDownloadOptions = {
  fileName?: string;
  mimeType?: string;
  urlApi?: Pick<typeof URL, "createObjectURL" | "revokeObjectURL">;
};

export function createExportDownload(
  bundle: MarginaliaExportV1,
  options: CreateExportDownloadOptions = {},
): PreparedExportDownload {
  const urlApi = options.urlApi ?? URL;
  const blob = createDocumentExportBlob(bundle, options.mimeType);
  const url = urlApi.createObjectURL(blob);
  let revoked = false;

  return {
    blob,
    fileName:
      options.fileName ??
      defaultDocumentExportFileName(bundle.document.fileName),
    url,
    revoke() {
      if (!revoked) {
        urlApi.revokeObjectURL(url);
        revoked = true;
      }
    },
  };
}

export function createLibraryExportDownload(
  bundle: MarginaliaLibraryExportV1,
  options: CreateExportDownloadOptions = {},
): PreparedExportDownload {
  const urlApi = options.urlApi ?? URL;
  const blob = createLibraryExportBlob(bundle, options.mimeType);
  const url = urlApi.createObjectURL(blob);
  let revoked = false;

  return {
    blob,
    fileName:
      options.fileName ?? defaultLibraryExportFileName(bundle.exportedAt),
    url,
    revoke() {
      if (!revoked) {
        urlApi.revokeObjectURL(url);
        revoked = true;
      }
    },
  };
}

export type DownloadExportOptions = CreateExportDownloadOptions & {
  document?: Pick<Document, "body" | "createElement">;
};

export function downloadExport(
  bundle: MarginaliaExportV1,
  options: DownloadExportOptions = {},
): PreparedExportDownload {
  const ownerDocument = options.document ?? document;
  const prepared = createExportDownload(bundle, options);
  const anchor = ownerDocument.createElement("a");
  anchor.href = prepared.url;
  anchor.download = prepared.fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  ownerDocument.body.append(anchor);

  try {
    anchor.click();
  } finally {
    anchor.remove();
    queueMicrotask(() => prepared.revoke());
  }

  return prepared;
}

export function downloadLibraryExport(
  bundle: MarginaliaLibraryExportV1,
  options: DownloadExportOptions = {},
): PreparedExportDownload {
  const ownerDocument = options.document ?? document;
  const prepared = createLibraryExportDownload(bundle, options);
  const anchor = ownerDocument.createElement("a");
  anchor.href = prepared.url;
  anchor.download = prepared.fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  ownerDocument.body.append(anchor);

  try {
    anchor.click();
  } finally {
    anchor.remove();
    queueMicrotask(() => prepared.revoke());
  }

  return prepared;
}
